/**
 * MenuBarPopover
 *
 * Compact timer panel rendered inside the Electron `menubar` window (the popover
 * anchored to the tray icon). Receives timer state via IPC only — it has no
 * Supabase auth or Pomodoro context of its own. Controls are forwarded back to
 * the main window through the contextBridge API.
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pause, Play, Square, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DeepProgressRing } from '@/components/focus/DeepProgressRing';
import type { DesktopTimerState } from '@/types/electron';

function formatClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function MenuBarPopover() {
  const [state, setState] = useState<DesktopTimerState | null>(null);

  useEffect(() => {
    const api = window.electronAPI;
    if (!api) return;
    return api.onTimerUpdate(setState);
  }, []);

  const api = window.electronAPI;
  const isRunning = state?.timerState === 'running';
  const isPaused = state?.timerState === 'paused';
  const isActive = isRunning || isPaused;

  const isManager = state?.timerMode === 'manager';
  const displayTime = state
    ? formatClock(isManager ? state.timeRemaining : state.elapsedTime)
    : '0:00';

  const totalSeconds = (state?.sessionDuration ?? 0) * 60;
  const progress = state && totalSeconds > 0
    ? isManager
      ? Math.min(((totalSeconds - state.timeRemaining) / totalSeconds) * 100, 100)
      : Math.min((state.elapsedTime / totalSeconds) * 100, 100)
    : 0;

  return (
    <div className="glass rounded-2xl p-4 w-full select-none text-foreground">
      <AnimatePresence mode="wait">
        {!isActive ? (
          <motion.div
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-3 py-3"
          >
            <p className="text-sm text-muted-foreground">No active session</p>
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={() => api?.showMainWindow()}
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
              Open Luma
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="active"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-4"
          >
            <DeepProgressRing progress={progress} size={56} strokeWidth={2.5}>
              <span className="text-[10px] font-mono font-semibold tabular-nums">
                {displayTime}
              </span>
            </DeepProgressRing>

            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
                {isManager ? 'Manager Mode' : 'Deep Focus'}
              </p>
              <p className="text-sm font-medium truncate">
                {state?.activeTaskName ?? 'Focus session'}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              {isRunning ? (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  aria-label="Pause"
                  onClick={() => api?.pauseTimer()}
                >
                  <Pause className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  aria-label="Resume"
                  onClick={() => api?.resumeTimer()}
                >
                  <Play className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive/70 hover:text-destructive"
                aria-label="Stop"
                onClick={() => api?.stopTimer()}
              >
                <Square className="h-3.5 w-3.5" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-3 pt-3 border-t border-border/50 flex justify-center">
        <button
          onClick={() => api?.showMainWindow()}
          className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        >
          Open Luma
        </button>
      </div>
    </div>
  );
}
